import {TManifestEnhanced, TManifestStopEnhanced} from "../components/common/types";
import {DispatchGroup, Manifest, ManifestAttributeFilter} from "../generated/graphql";
import {buildDispatchGroupFilter} from "./FilterUtils";

export const hasOpenStops = (stops: TManifestStopEnhanced[] | null | undefined): boolean => {
  if (stops) {
    return stops.some((x) => ["A", "Q", "N"].includes(x.jobStopStatus ?? ""));
  }
  return false;
};

export const buildManifestDriverGroupFilter = (driverGroups: DispatchGroup[]): ManifestAttributeFilter =>
  buildDispatchGroupFilter(
    "dispatchGroup_dispatchGroupId",
    driverGroups.map((dg) => dg.dispatchGroupId as number)
  ) as ManifestAttributeFilter;

/**
 * The replication Lambda leaves a manifest's stops out of the search index once its stop count
 * passes the tenant's cap, and stamps this alert on the document in their place — see the
 * ManifestAlerts CTE in nxtgen-services manifest-query.sql (and its -rds twin, identical). The cap
 * is a per-tenant ConfigCat setting owned by the backend, in a different ConfigCat config than the
 * one this UI reads, so the alert is the only place its effective value reaches the client.
 */
const STOPS_OMITTED_ALERT_PREFIX = "Stop data omitted";
const STOPS_OMITTED_CAP_PATTERN = /exceeds maximum of (\d+)/;

type ManifestLike = Manifest | TManifestEnhanced | null | undefined;

const findStopsOmittedAlert = (manifest: ManifestLike) =>
  manifest?.metadata?.alerts?.find((alert) => alert.message.startsWith(STOPS_OMITTED_ALERT_PREFIX));

export const hasTooManyStops = (manifest: ManifestLike): boolean => !!findStopsOmittedAlert(manifest);

/**
 * Returns true if the manifest has any job with multiple pickups AND multiple deliveries.
 * GraphHopper cannot model the correct stop ordering for such jobs.
 */
export const hasMultiPickupDeliveryJobs = (manifest: ManifestLike): boolean => {
  const stops = (manifest as Manifest | undefined)?.stops;
  if (!stops?.length) return false;

  const jobStopCounts = new Map<number, {pickups: number; deliveries: number}>();
  for (const stop of stops) {
    const jobId = stop.job?.jobId;
    if (jobId == null) continue;
    const counts = jobStopCounts.get(jobId) ?? {pickups: 0, deliveries: 0};
    if (stop.stopType === "P") counts.pickups++;
    else if (stop.stopType === "D") counts.deliveries++;
    jobStopCounts.set(jobId, counts);
  }

  for (const counts of jobStopCounts.values()) {
    if (counts.pickups > 1 && counts.deliveries > 1) return true;
  }
  return false;
};

/** The cap in force when the manifest was replicated; undefined when its stops came through intact. */
export const getMaxStopCount = (manifest: ManifestLike): number | undefined => {
  const matched = findStopsOmittedAlert(manifest)?.message.match(STOPS_OMITTED_CAP_PATTERN);
  return matched ? Number(matched[1]) : undefined;
};
